import { forwardRef, useImperativeHandle, type ReactNode } from 'react';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';
import { AnimatePresence, motion } from 'motion/react';
import { Icon, PlainButton } from '@customerio/pluma-components/react';
import * as styles from './index.css';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ConversationRef {
  scrollToBottom: () => void;
}

interface ConversationProps {
  /** Message content — rendered inside the scrollable area. */
  children: ReactNode;
  /** Rendered outside the scrollable area (e.g. ConversationScrollButton). */
  footer?: ReactNode;
  className?: string;
}

/**
 * Wraps chat messages and automatically sticks to the bottom as new
 * content streams in. Uses `use-stick-to-bottom` for smooth, frame-perfect
 * scrolling that avoids the jankiness of MutationObserver + scrollTo.
 *
 * Pass `<ConversationScrollButton />` via the `footer` prop so it renders
 * as a sibling of the scroll content, not inside it.
 */
export const Conversation = forwardRef<ConversationRef, ConversationProps>(({ children, footer, className }, ref) => {
  return (
    <StickToBottom className={`${styles.container} ${className ?? ''}`} resize="smooth" initial="smooth">
      <ConversationInner ref={ref}>{children}</ConversationInner>
      {footer}
    </StickToBottom>
  );
});

// Inner component that can access the stick-to-bottom context
const ConversationInner = forwardRef<ConversationRef, { children: ReactNode }>(({ children }, ref) => {
  const { scrollToBottom } = useStickToBottomContext();

  useImperativeHandle(
    ref,
    () => ({
      scrollToBottom: () => scrollToBottom(),
    }),
    [scrollToBottom],
  );

  return <StickToBottom.Content className={styles.scrollArea}>{children}</StickToBottom.Content>;
});

// ---------------------------------------------------------------------------
// Scroll button
// ---------------------------------------------------------------------------

export function ConversationScrollButton() {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  return (
    <AnimatePresence>
      {!isAtBottom ? (
        <motion.div
          className={styles.scrollButtonWrapper}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.15 }}
        >
          <PlainButton className={styles.scrollButton} onClick={() => scrollToBottom()} aria-label="Scroll to bottom">
            <Icon name="chevron-down" size="sm" />
          </PlainButton>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
